import { Component, Input, Output, EventEmitter, forwardRef, OnInit, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface TimePickerConfig {
  label: string;
  value?: string;
  placeholder?: string;
  id: string;
  name: string;
  min?: string;
  max?: string;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  autoClose?: boolean;
}

@Component({
  selector: 'app-time-picker',
  standalone: false,
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true
    }
  ]
})
export class TimePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() config!: TimePickerConfig;
  @Input() label?: string;
  @Input() value: string = '';
  @Input() placeholder: string = 'Select time';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() min?: string;
  @Input() max?: string;
  @Input() step?: number;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() autoClose: boolean = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() blur = new EventEmitter<FocusEvent>();

  showDropdown = false;
  isFocused: boolean = false;
  dropdownTop: number = 0;
  dropdownRight: number = 0;

  hours: string[] = [];
  minutes: string[] = [];
  periods: string[] = ['AM', 'PM'];

  selectedHour: string = '12';
  selectedMinute: string = '00';
  selectedPeriod: string = 'AM';
  currentInputValue: string = ''; // Track what user is typing
  previousInputValue: string = ''; // Track previous value to detect deletion
  isValid: boolean = true;
  errorMessage: string = '';
  isTouched: boolean = false;

  private onChange = (value: any) => { };
  private onTouched = () => { };
  private _captureClickHandler = (event: Event) => this._handleCaptureClick(event);

  constructor(private elementRef: ElementRef) {
    this.generateLists();
  }

  ngOnInit(): void {
    if (this.value) {
      this.parseTime(this.value);
      this.currentInputValue = this.displayValue;
      this.previousInputValue = this.displayValue;
    }
    // capture-phase listener to detect clicks even when other elements stopPropagation
    document.addEventListener('click', this._captureClickHandler, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._captureClickHandler, true);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  private _handleCaptureClick(event: Event): void {
    const target = (event && (event as any).target) as Node | null;
    if (!target) return;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.showDropdown = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.showDropdown) {
      this.calculateDropdownPosition();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.showDropdown) {
      this.calculateDropdownPosition();
    }
  }

  generateLists(): void {
    // Generate hours 01-12
    this.hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    // Generate minutes 00-59
    this.minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  }

  toggleDropdown(): void {
    if (!this.disabled && !this.readonly) {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.calculateDropdownPosition();
        // Initialize selections when opening:
        // Prefer parsing bound value, else fallback to device time
        const initial = this.determineInitialSelection();
        this.selectedHour = initial.hour;
        this.selectedMinute = initial.minute;
        this.selectedPeriod = initial.period;
        // scroll so selections are visible (use slight top offset)
        setTimeout(() => {
          // Ensure hour is aligned to top for clarity, then make other columns visible
          this.scrollHourToTop();
          this.scrollToSelection('start');
        }, 0);
      }
    }
  }

  calculateDropdownPosition(): void {
    const inputElement = this.elementRef.nativeElement.querySelector('input');
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownHeight = 240; // Approximate height of dropdown
      const dropdownWidth = 240;

      // Calculate top position (below the input)
      let top = rect.bottom + 6; // 6px gap

      // If dropdown would go off bottom of screen, position above input
      if (top + dropdownHeight > viewportHeight) {
        top = rect.top - dropdownHeight - 6;
        // If still off screen, position at top of viewport
        if (top < 0) {
          top = 10;
        }
      }

      // Calculate right position (align with input right edge)
      let right = viewportWidth - rect.right;

      // If dropdown would go off right of screen, align with left edge
      if (right + dropdownWidth > viewportWidth) {
        right = viewportWidth - rect.left - dropdownWidth;
        // If still off screen, position at left edge
        if (right < 0) {
          right = 10;
        }
      }

      this.dropdownTop = top;
      this.dropdownRight = right;
    }
  }

  selectHour(hour: string, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    // update selection highlight only; do not commit until Done
    this.selectedHour = hour;
    this.isTouched = true;
    // ensure clicked item is visible
    setTimeout(() => this.scrollToSelection('center'), 0);
  }

  selectMinute(minute: string, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    // Validate minute before setting
    const minuteNum = parseInt(minute, 10);
    if (minuteNum >= 0 && minuteNum <= 59) {
      // update selection highlight only; commit on Done
      this.selectedMinute = minute;
      this.isTouched = true;
      // ensure clicked item is visible
      setTimeout(() => this.scrollToSelection('center'), 0);
    }
  }

  selectPeriod(period: string, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    // update selection highlight only; commit on Done
    this.selectedPeriod = period;
    this.isTouched = true;
    // ensure clicked item is visible
    setTimeout(() => this.scrollToSelection(), 0);
  }

  updateValue(): void {
    // Validate minute before updating
    const minuteNum = parseInt(this.selectedMinute, 10);
    if (minuteNum < 0 || minuteNum > 59) {
      this.isValid = false;
      this.errorMessage = 'Minute must be between 00 and 59';
      return;
    }
    
    let hour = parseInt(this.selectedHour, 10);
    if (this.selectedPeriod === 'PM' && hour !== 12) {
      hour += 12;
    } else if (this.selectedPeriod === 'AM' && hour === 12) {
      hour = 0;
    }

    const hourStr = String(hour).padStart(2, '0');
    const timeValue = `${hourStr}:${this.selectedMinute}`;

    this.value = timeValue;
    // Validate the new value
    this.validateInput(this.displayValue);
    this.onChange(timeValue);
    this.valueChange.emit(timeValue);
  }

  confirmSelection(): void {
    // Build 24h value from current selected values
    let h = parseInt(this.selectedHour, 10);
    if (this.selectedPeriod === 'PM' && h !== 12) h += 12;
    if (this.selectedPeriod === 'AM' && h === 12) h = 0;
    const hourStr = String(h).padStart(2, '0');
    const minStr = String(this.selectedMinute).padStart(2, '0');
    const timeValue = `${hourStr}:${minStr}`;
    this.value = timeValue;
    // Update display and emit value
    this.currentInputValue = this.formatTime(timeValue);
    this.previousInputValue = this.currentInputValue;
    this.validateInput(this.currentInputValue);
    this.onChange(timeValue);
    this.valueChange.emit(timeValue);
    // close dropdown
    this.showDropdown = false;
  }

  parseTime(value: string): void {
    if (!value) return;
    const [hStr, mStr] = value.split(':');
    let h = parseInt(hStr, 10);
    const m = mStr;

    if (isNaN(h)) return;

    if (h >= 12) {
      this.selectedPeriod = 'PM';
      if (h > 12) h -= 12;
    } else {
      this.selectedPeriod = 'AM';
      if (h === 0) h = 12;
    }

    this.selectedHour = String(h).padStart(2, '0');
    this.selectedMinute = m || '00';
  }

  private determineInitialSelection(): { hour: string; minute: string; period: string } {
    // 1) Try to parse displayValue (handles AM/PM formats)
    try {
      const parsed = this.parseManualTime(this.displayValue);
      if (parsed) return parsed;
    } catch {
      // ignore
    }

    // 2) Try to parse raw value (HH:mm 24h)
    if (this.value) {
      const parts = this.value.split(':');
      if (parts.length >= 2) {
        const h24 = parseInt(parts[0], 10);
        const m = parts[1].substring(0, 2);
        if (!isNaN(h24)) {
          const period = h24 >= 12 ? 'PM' : 'AM';
          let h12 = h24;
          if (h24 === 0) h12 = 12;
          else if (h24 > 12) h12 = h24 - 12;
          return { hour: String(h12).padStart(2, '0'), minute: String(m).padStart(2, '0'), period };
        }
      }
    }

    // 3) Fallback to device current time
    const now = new Date();
    let h = now.getHours();
    const m = now.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h = h - 12;
    return { hour: String(h).padStart(2, '0'), minute: String(m).padStart(2, '0'), period };
  }

  scrollToSelection(align: 'center' | 'start' = 'center'): void {
    try {
      const host = this.elementRef.nativeElement as HTMLElement;
      const scrollCols = host.querySelectorAll('.overflow-y-auto');
      if (!scrollCols || scrollCols.length === 0) return;

      const scrollToMatch = (container: Element | null, text: string) => {
        if (!container || !text) return;
        const el = container as HTMLElement;
        const children = Array.from(el.children) as HTMLElement[];
        const match = children.find(ch => (ch.textContent || '').trim() === String(text).trim());
        if (!match) return;

        if (align === 'center') {
          // center using scrollIntoView for smooth animation
          match.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        // align === 'start' but apply a small offset so the selected item isn't flush against the top
        const containerTop = el.scrollTop;
        const matchOffset = match.offsetTop;
        // place match slightly below top (15% of container height)
        const offset = Math.round(el.clientHeight * 0.15);
        const targetScrollTop = Math.max(0, matchOffset - offset);
        el.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      };

      // Hours = first column, Minutes = second, Periods = third
      scrollToMatch(scrollCols[0] || null, this.selectedHour);
      if (scrollCols.length > 1) scrollToMatch(scrollCols[1], this.selectedMinute);
      if (scrollCols.length > 2) scrollToMatch(scrollCols[2], this.selectedPeriod);
    } catch (e) {
      // ignore errors
    }
  }

  private scrollHourToTop(): void {
    try {
      const host = this.elementRef.nativeElement as HTMLElement;
      const scrollCols = host.querySelectorAll('.overflow-y-auto');
      if (!scrollCols || scrollCols.length === 0) return;
      const hoursCol = scrollCols[0] as HTMLElement;
      const children = Array.from(hoursCol.children) as HTMLElement[];
      const match = children.find(ch => (ch.textContent || '').trim() === String(this.selectedHour).trim());
      if (!match) return;
      // Align selected hour to the very top (no offset)
      const targetScrollTop = match.offsetTop;
      hoursCol.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    } catch {
      // ignore
    }
  }

  onBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.isTouched = true;
    
    // On blur, try to parse and format the input value
    if (!this.isReadonly && !this.disabled) {
      const target = event.target as HTMLInputElement;
      const inputValue = target.value.trim();
      
      // Validate on blur
      this.validateInput(inputValue);
      
      if (inputValue) {
        const parsed = this.parseManualTime(inputValue);
        if (parsed) {
          this.selectedHour = parsed.hour;
          this.selectedMinute = parsed.minute;
          this.selectedPeriod = parsed.period;
          this.updateValue();
          // Format the display value
          target.value = this.displayValue;
          this.currentInputValue = this.displayValue;
          // Re-validate after formatting
          this.validateInput(this.displayValue);
        } else {
          // Invalid format - try to keep what user typed, or restore previous valid value
          if (this.value && inputValue.length === 0) {
            // Only restore if input was cleared
            target.value = this.displayValue;
            this.currentInputValue = this.displayValue;
            this.validateInput(this.displayValue);
          } else {
            // Keep user's input even if invalid (they might come back to fix it)
            this.currentInputValue = inputValue;
            this.validateInput(inputValue);
          }
        }
      } else {
        // Empty input - clear value
        this.value = '';
        this.currentInputValue = '';
        this.onChange('');
        this.valueChange.emit('');
        this.validateInput('');
      }
    }
    
    this.onTouched();
    this.blur.emit(event);
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
  }

  onInputChange(event: Event): void {
    if (this.isReadonly || this.disabled) return;
    
    const target = event.target as HTMLInputElement;
    let inputValue = target.value; // Preserve user's exact input while typing
    const cursorPos = target.selectionStart || 0;
    
    // Check if user is deleting (value length decreased)
    const isDeleting = inputValue.length < this.previousInputValue.length;
    
    this.isTouched = true;
    
    // Validate and limit hour to maximum of 12
    // Check if input contains hour part that's greater than 12
    const colonIndex = inputValue.indexOf(':');
    if (colonIndex !== -1 || (!inputValue.includes(':') && inputValue.length <= 2)) {
      const hourPart = colonIndex === -1 ? inputValue : inputValue.substring(0, colonIndex);
      const digitsOnly = hourPart.replace(/\D/g, '');
      
      if (digitsOnly.length > 0) {
        const hourValue = parseInt(digitsOnly, 10);
        
        // If hour is greater than 12, limit it to 12
        if (hourValue > 12) {
          const correctedHour = '12';
          if (colonIndex === -1) {
            // No colon yet, replace the hour part
            inputValue = correctedHour;
          } else {
            // Has colon, replace hour part and keep the rest
            const restOfInput = inputValue.substring(colonIndex);
            inputValue = correctedHour + restOfInput;
          }
          target.value = inputValue;
          // Move cursor to after the corrected hour
          setTimeout(() => {
            const newCursorPos = colonIndex === -1 ? correctedHour.length : colonIndex + 1;
            target.setSelectionRange(newCursorPos, newCursorPos);
          }, 0);
        }
      }
    }
    
    // Auto-format: Add colon after 2 digits for hour (if not already present)
    // Only do this when user is typing (not deleting)
    // Pattern: "12" -> "12:" (when user types second digit)
    if (!isDeleting && inputValue.length === 2 && !inputValue.includes(':')) {
      // Check if it's two digits (hour)
      const digitsOnly = inputValue.replace(/\D/g, '');
      if (digitsOnly.length === 2) {
        const hour = parseInt(digitsOnly, 10);
        if (hour >= 1 && hour <= 12) {
          inputValue = digitsOnly + ':';
          target.value = inputValue;
          this.currentInputValue = inputValue;
          // Move cursor after colon
          setTimeout(() => {
            target.setSelectionRange(3, 3);
          }, 0);
        }
      }
    }
    
    // Validate and limit minute to maximum of 59
    // Check if input contains minute part that's greater than 59
    // Recalculate colonIndex in case inputValue was modified
    const colonIndexForMinute = inputValue.indexOf(':');
    if (colonIndexForMinute !== -1 && inputValue.length > colonIndexForMinute + 1) {
      const parts = inputValue.split(':');
      if (parts.length === 2 && parts[1]) {
        // Get minute part (remove AM/PM and spaces)
        const minutePart = parts[1].replace(/\s*(AM|PM).*/i, '').trim();
        const minuteDigitsOnly = minutePart.replace(/\D/g, '').substring(0, 2); // Max 2 digits
        
        if (minuteDigitsOnly.length > 0) {
          const minuteValue = parseInt(minuteDigitsOnly, 10);
          
          // If minute is greater than 59, limit it to 59
          if (minuteValue > 59) {
            const correctedMinute = '59';
            // Preserve AM/PM if present
            const amPmMatch = parts[1].match(/\s*(AM|PM)/i);
            const amPmPart = amPmMatch ? ' ' + amPmMatch[1].toUpperCase() : '';
            const newMinutePart = correctedMinute + amPmPart;
            inputValue = parts[0] + ':' + newMinutePart;
            target.value = inputValue;
            // Move cursor to after the corrected minute
            setTimeout(() => {
              const newCursorPos = colonIndexForMinute + 1 + correctedMinute.length;
              target.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
          }
        }
      }
    }
    
    // Auto-format: Add colon after 3 characters if pattern is "HH:" (hour already has colon)
    // This handles cases where user types "12:" and continues
    if (inputValue.length >= 3 && inputValue.includes(':') && inputValue.split(':').length === 2) {
      const parts = inputValue.split(':');
      if (parts[0].length === 2 && parts[1] && parts[1].length > 0) {
        // Validate and format minutes
        const minuteStr = parts[1].replace(/\D/g, '').substring(0, 2); // Max 2 digits
        if (minuteStr.length > 0) {
          const minute = parseInt(minuteStr, 10);
          if (minute >= 0 && minute <= 59) {
            // Valid minute, keep as is
            this.currentInputValue = parts[0] + ':' + minuteStr;
          } else if (minute > 59) {
            // Invalid minute (>59), limit to 59
            this.currentInputValue = parts[0] + ':59';
            target.value = this.currentInputValue;
            setTimeout(() => {
              target.setSelectionRange(this.currentInputValue.length, this.currentInputValue.length);
            }, 0);
          }
        }
      }
    }
    
    // Auto-convert lowercase "am" or "pm" to uppercase as user types
    if (inputValue && !isDeleting) {
      // Convert any occurrence of "am" or "pm" (case-insensitive) to uppercase
      // This handles cases like "12:30 am", "12:30pm", "12:30 Am", etc.
      const amPmPattern = /(am|pm)/gi;
      const newValue = inputValue.replace(amPmPattern, (match) => match.toUpperCase());
      
      if (newValue !== inputValue) {
        inputValue = newValue;
        target.value = inputValue;
        // Preserve cursor position
        setTimeout(() => {
          target.setSelectionRange(cursorPos, cursorPos);
        }, 0);
      }
    }
    
    this.currentInputValue = inputValue;
    
    // Validate input in real-time
    this.validateInput(inputValue);
    
    // Only parse and update internal value if we have a complete valid format
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      const parsed = this.parseManualTime(trimmedValue);
      if (parsed) {
        // Update internal state
        this.selectedHour = parsed.hour;
        this.selectedMinute = parsed.minute;
        this.selectedPeriod = parsed.period;
        this.updateValue();
      }
    } else {
      // Clear value if input is empty
      this.value = '';
      this.currentInputValue = '';
      this.onChange('');
      this.valueChange.emit('');
      this.validateInput('');
    }
    
    // Update previous value for next comparison
    this.previousInputValue = inputValue;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.isReadonly || this.disabled) return;
    
    const target = event.target as HTMLInputElement;
    const cursorPos = target.selectionStart || 0;
    const inputValue = target.value;
    
    // Handle backspace to remove colon automatically
    if (event.key === 'Backspace') {
      if (cursorPos > 0 && cursorPos <= inputValue.length) {
        const charBeforeCursor = inputValue[cursorPos - 1];
        const charAtCursor = inputValue[cursorPos];
        
        // Case 1: If the character before cursor is a colon, remove it
        if (charBeforeCursor === ':') {
          event.preventDefault();
          // Remove colon (e.g., "12:" -> "12" when cursor is at position 3)
          const newValue = inputValue.substring(0, cursorPos - 1) + inputValue.substring(cursorPos);
          target.value = newValue;
          this.currentInputValue = newValue;
          // Set cursor position after deletion (move back by 1)
          setTimeout(() => {
            target.setSelectionRange(cursorPos - 1, cursorPos - 1);
          }, 0);
          // Update state directly without triggering auto-format
          this.updateStateFromInput(newValue);
          return;
        }
        
        // Case 2: If the character before cursor is a digit and the next character is a colon, remove both
        if (/\d/.test(charBeforeCursor) && charAtCursor === ':') {
          event.preventDefault();
          // Remove both digit and colon (e.g., "12:" -> "1" when cursor is at position 2)
          const newValue = inputValue.substring(0, cursorPos - 1) + inputValue.substring(cursorPos + 1);
          target.value = newValue;
          this.currentInputValue = newValue;
          // Set cursor position after deletion (move back by 1)
          setTimeout(() => {
            target.setSelectionRange(cursorPos - 1, cursorPos - 1);
          }, 0);
          // Update state directly without triggering auto-format
          this.updateStateFromInput(newValue);
          return;
        }
      }
      // Allow normal backspace for other cases
      return;
    }
    
    // Validate hour input - prevent entering hours greater than 12
    if (/\d/.test(event.key)) {
      const colonIndex = inputValue.indexOf(':');
      const isTypingInHourField = colonIndex === -1 || cursorPos <= colonIndex;
      
      if (isTypingInHourField) {
        // Get the hour part (before colon or entire input if no colon)
        const hourPart = colonIndex === -1 ? inputValue : inputValue.substring(0, colonIndex);
        
        // Calculate what the hour would be after typing this digit
        const beforeCursor = hourPart.substring(0, cursorPos);
        const afterCursor = hourPart.substring(cursorPos);
        const newHourPart = beforeCursor + event.key + afterCursor;
        
        // Remove any non-digits and check the hour value
        const digitsOnly = newHourPart.replace(/\D/g, '');
        if (digitsOnly.length > 0) {
          const hourValue = parseInt(digitsOnly, 10);
          
          // Block if hour would be greater than 12
          if (hourValue > 12) {
            event.preventDefault();
            return;
          }
        }
      } else {
        // Typing in minute field (after colon)
        if (colonIndex !== -1) {
          const minutePart = inputValue.substring(colonIndex + 1);
          // Remove any non-digit characters (like spaces, AM/PM) to get just the minute digits
          const minuteDigitsOnly = minutePart.replace(/\D/g, '');
          
          // Calculate what the minute would be after typing this digit
          const beforeCursor = minutePart.substring(0, cursorPos - colonIndex - 1);
          const afterCursor = minutePart.substring(cursorPos - colonIndex - 1);
          const newMinutePart = beforeCursor + event.key + afterCursor;
          const newMinuteDigitsOnly = newMinutePart.replace(/\D/g, '');
          
          if (newMinuteDigitsOnly.length > 0) {
            const minuteValue = parseInt(newMinuteDigitsOnly, 10);
            
            // Block if minute would be greater than 59
            if (minuteValue > 59) {
              event.preventDefault();
              return;
            }
            
            // Block if first digit is 6-9 and user is trying to type a second digit
            // This prevents typing "60", "70", "80", "90", etc.
            if (newMinuteDigitsOnly.length === 1) {
              const firstDigit = parseInt(newMinuteDigitsOnly[0], 10);
              if (firstDigit >= 6 && firstDigit <= 9) {
                // First digit is 6-9, any second digit would make it 60-99, block it
                event.preventDefault();
                return;
              }
            }
          }
        }
      }
    }
    
    // Allow all other normal editing keys
    const allowedKeys = ['Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 
                        'Home', 'End', 'Tab', 'Escape'];
    if (allowedKeys.includes(event.key)) {
      return; // Allow these keys to work normally
    }
    
    // Prevent dropdown toggle on Enter, but allow normal input
    if (event.key === 'Enter') {
      event.preventDefault();
      target.blur();
    }
  }

  updateStateFromInput(inputValue: string): void {
    // Update state from input without triggering auto-format logic
    this.isTouched = true;
    
    // Validate input
    this.validateInput(inputValue);
    
    // Try to parse and update internal value if we have a complete valid format
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      const parsed = this.parseManualTime(trimmedValue);
      if (parsed) {
        // Update internal state
        this.selectedHour = parsed.hour;
        this.selectedMinute = parsed.minute;
        this.selectedPeriod = parsed.period;
        this.updateValue();
      }
    } else {
      // Clear value if input is empty
      this.value = '';
      this.currentInputValue = '';
      this.onChange('');
      this.valueChange.emit('');
      this.validateInput('');
    }
    
    // Update previous value for next comparison
    this.previousInputValue = inputValue;
  }

  parseManualTime(input: string): { hour: string; minute: string; period: string } | null {
    // Remove extra spaces and convert to uppercase
    const cleaned = input.trim().toUpperCase();
    
    // Try various formats: "HH:MM AM/PM", "HH:MM", "H:MM AM/PM", etc.
    // Pattern 1: "HH:MM AM" or "HH:MM PM" or "H:MM AM" or "H:MM PM"
    let match = cleaned.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      const period = match[3];
      
      if (hour >= 1 && hour <= 12 && parseInt(minute, 10) >= 0 && parseInt(minute, 10) <= 59) {
        return {
          hour: String(hour).padStart(2, '0'),
          minute: minute,
          period: period
        };
      }
    }
    
    // Pattern 2: "HH:MM" (24-hour format) - convert to 12-hour
    match = cleaned.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      
      if (hour >= 0 && hour <= 23 && parseInt(minute, 10) >= 0 && parseInt(minute, 10) <= 59) {
        let period = 'AM';
        if (hour >= 12) {
          period = 'PM';
          if (hour > 12) hour -= 12;
        } else if (hour === 0) {
          hour = 12;
        }
        
        return {
          hour: String(hour).padStart(2, '0'),
          minute: minute,
          period: period
        };
      }
    }
    
    // Pattern 3: "HHMM AM/PM" or "HMM AM/PM" (no colon)
    match = cleaned.match(/^(\d{1,2})(\d{2})\s*(AM|PM)$/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      const period = match[3];
      
      if (hour >= 1 && hour <= 12 && parseInt(minute, 10) >= 0 && parseInt(minute, 10) <= 59) {
        return {
          hour: String(hour).padStart(2, '0'),
          minute: minute,
          period: period
        };
      }
    }
    
    // Pattern 4: "HHMM" (24-hour, no colon) - convert to 12-hour
    match = cleaned.match(/^(\d{1,2})(\d{2})$/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      
      if (hour >= 0 && hour <= 23 && parseInt(minute, 10) >= 0 && parseInt(minute, 10) <= 59) {
        let period = 'AM';
        if (hour >= 12) {
          period = 'PM';
          if (hour > 12) hour -= 12;
        } else if (hour === 0) {
          hour = 12;
        }
        
        return {
          hour: String(hour).padStart(2, '0'),
          minute: minute,
          period: period
        };
      }
    }
    
    return null; // Invalid format
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get displayValue(): string {
    if (!this.value) return '';
    return this.formatTime(this.value);
  }

  formatTime(value: string): string {
    if (!value) return '';
    try {
      const [hStr, mStr] = value.split(':');
      let h = parseInt(hStr, 10);
      const m = mStr;
      const period = h >= 12 ? 'PM' : 'AM';

      if (h > 12) h -= 12;
      if (h === 0) h = 12;

      return `${String(h).padStart(2, '0')}:${m} ${period}`;
    } catch (e) {
      return value;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    if (this.value) {
      this.parseTime(this.value);
      this.currentInputValue = this.displayValue;
      this.previousInputValue = this.displayValue;
      // Validate when value is set programmatically
      this.validateInput(this.displayValue);
    } else {
      this.currentInputValue = '';
      this.previousInputValue = '';
      this.validateInput('');
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get displayLabel(): string {
    return this.config?.label || this.label || '';
  }

  get displayId(): string {
    return this.config?.id || this.id || '';
  }

  get displayName(): string {
    return this.config?.name || this.name || '';
  }

  get displayPlaceholder(): string {
    return this.config?.placeholder || this.placeholder || 'Select time';
  }

  get isRequired(): boolean {
    return this.config?.required ?? this.required;
  }

  get isDisabled(): boolean {
    return this.config?.disabled ?? this.disabled;
  }

  get isReadonly(): boolean {
    return this.config?.readonly ?? this.readonly;
  }

  get hasError(): boolean {
    return this.isTouched && !this.isValid;
  }

  validateInput(input: string): void {
    const trimmedInput = input.trim();
    
    // Required validation
    if (this.isRequired && !trimmedInput) {
      this.isValid = false;
      this.errorMessage = 'Time is required';
      return;
    }
    
    // If empty and not required, it's valid
    if (!trimmedInput) {
      this.isValid = true;
      this.errorMessage = '';
      return;
    }
    
    // Try to parse the input
    const parsed = this.parseManualTime(trimmedInput);
    if (!parsed) {
      // Check if it's a partial input (e.g., "12:" or "12:3")
      const colonIndex = trimmedInput.indexOf(':');
      if (colonIndex > 0) {
        const hourPart = trimmedInput.substring(0, colonIndex);
        const minutePart = trimmedInput.substring(colonIndex + 1);
        const hour = parseInt(hourPart, 10);
        const minute = parseInt(minutePart, 10);
        
        // Validate hour
        if (isNaN(hour) || hour < 1 || hour > 12) {
          this.isValid = false;
          this.errorMessage = 'Hour must be between 1 and 12';
          return;
        }
        
        // Validate minute if provided
        if (minutePart.length > 0) {
          if (isNaN(minute) || minute < 0 || minute > 59) {
            this.isValid = false;
            this.errorMessage = 'Minute must be between 00 and 59';
            return;
          }
        } else {
          // Partial input - still typing minutes, don't show error yet
          this.isValid = true;
          this.errorMessage = '';
          return;
        }
      } else {
        // No colon, might be typing hour
        const digitsOnly = trimmedInput.replace(/\D/g, '');
        if (digitsOnly.length > 0 && digitsOnly.length <= 2) {
          const hour = parseInt(digitsOnly, 10);
          if (hour >= 1 && hour <= 12) {
            // Valid hour, still typing
            this.isValid = true;
            this.errorMessage = '';
            return;
          }
        }
      }
      
      this.isValid = false;
      this.errorMessage = 'Invalid time format. Use HH:MM AM/PM or HH:MM (24-hour)';
      return;
    }
    
    // Validate hour range
    const hour = parseInt(parsed.hour, 10);
    if (hour < 1 || hour > 12) {
      this.isValid = false;
      this.errorMessage = 'Hour must be between 1 and 12';
      return;
    }
    
    // Validate minute range (0-59)
    const minute = parseInt(parsed.minute, 10);
    if (isNaN(minute) || minute < 0 || minute > 59) {
      this.isValid = false;
      this.errorMessage = 'Minute must be between 00 and 59';
      return;
    }
    
    // Validate min/max time constraints if provided
    if (this.min || this.max) {
      const timeValue = this.convertTo24Hour(parsed.hour, parsed.minute, parsed.period);
      const timeStr = `${timeValue.hour}:${timeValue.minute}`;
      
      if (this.min && timeStr < this.min) {
        this.isValid = false;
        this.errorMessage = `Time must be after ${this.formatTime(this.min)}`;
        return;
      }
      
      if (this.max && timeStr > this.max) {
        this.isValid = false;
        this.errorMessage = `Time must be before ${this.formatTime(this.max)}`;
        return;
      }
    }
    
    // All validations passed
    this.isValid = true;
    this.errorMessage = '';
  }

  convertTo24Hour(hour: string, minute: string, period: string): { hour: string; minute: string } {
    let h = parseInt(hour, 10);
    if (period === 'PM' && h !== 12) {
      h += 12;
    } else if (period === 'AM' && h === 12) {
      h = 0;
    }
    return {
      hour: String(h).padStart(2, '0'),
      minute: minute
    };
  }
}
